import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { Container, Row, Col, Card, Button, Alert, Badge, Spinner } from 'react-bootstrap';
import { useAuth } from '../utils/AuthContext';

const Subscription = () => {
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [subscription, setSubscription] = useState(null);
  const { currentUser } = useAuth();
  
  // Fetch current subscription status
  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);
  
  const fetchSubscriptionStatus = async () => {
    try {
      setPageLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/subscription/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSubscription(response.data.subscription);
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
    } finally {
      setPageLoading(false);
    }
  };
  
  const handleSubscribe = async (planType) => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/create-checkout-session', 
        { planType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        setError(response.data.message || 'Failed to initiate checkout process');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error initiating subscription');
    } finally {
      setLoading(false);
    }
  };
  
  const handleManageBilling = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/create-portal-session', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        setError('Failed to open billing portal');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error opening billing portal');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.')) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/subscription/cancel', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSuccess('Subscription will be canceled at the end of your billing period');
        fetchSubscriptionStatus();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error canceling subscription');
    } finally {
      setLoading(false);
    }
  };
  
  const handleReactivateSubscription = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/subscription/reactivate', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSuccess('Subscription reactivated successfully!');
        fetchSubscriptionStatus();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error reactivating subscription');
    } finally {
      setLoading(false);
    }
  };
  
  const currentPlan = subscription?.plan || currentUser?.plan || 'free';
  const isActive = subscription?.status === 'active' || subscription?.subscriptionStatus === 'active';
  const isCanceling = subscription?.cancelAtPeriodEnd;
  
  if (pageLoading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }
  
  return (
    <Container className="mt-4">
      <h2 className="text-center mb-4">Subscription Plans</h2>
      
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}
      
      {/* Current subscription info */}
      {isActive && (
        <Card className="mb-4 border-success">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5>
                  Current Plan: <Badge bg="success">{currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}</Badge>
                  {isCanceling && <Badge bg="warning" className="ms-2">Canceling</Badge>}
                </h5>
                {subscription?.currentPeriodEnd && (
                  <p className="mb-0 text-muted">
                    {isCanceling ? 'Access until: ' : 'Renews: '}
                    {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                  </p>
                )}
              </div>
              <div>
                {isCanceling ? (
                  <Button variant="success" onClick={handleReactivateSubscription} disabled={loading}>
                    {loading ? 'Processing...' : 'Reactivate'}
                  </Button>
                ) : (
                  <>
                    <Button variant="outline-primary" onClick={handleManageBilling} disabled={loading} className="me-2">
                      Manage Billing
                    </Button>
                    <Button variant="outline-danger" onClick={handleCancelSubscription} disabled={loading}>
                      Cancel
                    </Button>
                  </>
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
      )}
      
      {/* Plan cards */}
      <Row className="justify-content-center">
        <Col md={4} className="mb-4">
          <Card className={`h-100 ${currentPlan === 'free' ? 'border-primary' : ''}`}>
            <Card.Header as="h5" className={`text-center ${currentPlan === 'free' ? 'bg-primary text-white' : ''}`}>
              Free Plan
            </Card.Header>
            <Card.Body>
              <Card.Title className="text-center display-6">$0<small className="text-muted">/month</small></Card.Title>
              <hr />
              <ul className="list-unstyled">
                <li className="mb-2">✓ 5 meetings per month</li>
                <li className="mb-2">✓ 60 minutes processing time</li>
                <li className="mb-2">✓ Basic transcription</li>
                <li className="mb-2">✓ AI summaries</li>
                <li className="text-muted">✗ Priority processing</li>
                <li className="text-muted">✗ API access</li>
              </ul>
            </Card.Body>
            <Card.Footer className="text-center">
              {currentPlan === 'free' ? (
                <Button variant="outline-primary" disabled>Current Plan</Button>
              ) : (
                <Button variant="outline-secondary" disabled>Downgrade</Button>
              )}
            </Card.Footer>
          </Card>
        </Col>
        
        <Col md={4} className="mb-4">
          <Card className={`h-100 ${currentPlan === 'pro' ? 'border-primary' : 'border-warning'}`}>
            <Card.Header as="h5" className={`text-center ${currentPlan === 'pro' ? 'bg-primary text-white' : 'bg-warning'}`}>
              Pro Plan <Badge bg="light" text="dark" className="ms-2">Popular</Badge>
            </Card.Header>
            <Card.Body>
              <Card.Title className="text-center display-6">$29<small className="text-muted">/month</small></Card.Title>
              <hr />
              <ul className="list-unstyled">
                <li className="mb-2">✓ 50 meetings per month</li>
                <li className="mb-2">✓ 600 minutes processing time</li>
                <li className="mb-2">✓ Advanced transcription</li>
                <li className="mb-2">✓ AI summaries & action items</li>
                <li className="mb-2">✓ Priority processing</li>
                <li className="text-muted">✗ API access</li>
              </ul>
            </Card.Body>
            <Card.Footer className="text-center">
              {currentPlan === 'pro' ? (
                <Button variant="outline-primary" disabled>Current Plan</Button>
              ) : (
                <Button 
                  variant="warning" 
                  onClick={() => handleSubscribe('pro')}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : currentPlan === 'enterprise' ? 'Downgrade' : 'Upgrade Now'}
                </Button>
              )}
            </Card.Footer>
          </Card>
        </Col>
        
        <Col md={4} className="mb-4">
          <Card className={`h-100 ${currentPlan === 'enterprise' ? 'border-primary' : ''}`}>
            <Card.Header as="h5" className={`text-center ${currentPlan === 'enterprise' ? 'bg-primary text-white' : 'bg-dark text-white'}`}>
              Enterprise Plan
            </Card.Header>
            <Card.Body>
              <Card.Title className="text-center display-6">$99<small className="text-muted">/month</small></Card.Title>
              <hr />
              <ul className="list-unstyled">
                <li className="mb-2">✓ 1000 meetings per month</li>
                <li className="mb-2">✓ 10,000 minutes processing time</li>
                <li className="mb-2">✓ Advanced transcription</li>
                <li className="mb-2">✓ AI summaries & action items</li>
                <li className="mb-2">✓ Priority processing</li>
                <li className="mb-2">✓ API access</li>
                <li className="mb-2">✓ Dedicated support</li>
              </ul>
            </Card.Body>
            <Card.Footer className="text-center">
              {currentPlan === 'enterprise' ? (
                <Button variant="outline-primary" disabled>Current Plan</Button>
              ) : (
                <Button 
                  variant="dark" 
                  onClick={() => handleSubscribe('enterprise')}
                  disabled={loading}
                >
                  {loading ? 'Processing...' : 'Upgrade Now'}
                </Button>
              )}
            </Card.Footer>
          </Card>
        </Col>
      </Row>
      
      {/* Usage info */}
      {subscription?.usage && (
        <Card className="mt-4">
          <Card.Header>Usage This Month</Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <p>
                  <strong>Meetings Processed:</strong> {subscription.usage.meetingsProcessed} / {subscription.limits?.meetings || 5}
                </p>
                <div className="progress">
                  <div 
                    className="progress-bar" 
                    style={{ width: `${Math.min(100, (subscription.usage.meetingsProcessed / (subscription.limits?.meetings || 5)) * 100)}%` }}
                  />
                </div>
              </Col>
              <Col md={6}>
                <p>
                  <strong>Minutes Processed:</strong> {subscription.usage.minutesProcessed} / {subscription.limits?.minutes || 60}
                </p>
                <div className="progress">
                  <div 
                    className="progress-bar" 
                    style={{ width: `${Math.min(100, (subscription.usage.minutesProcessed / (subscription.limits?.minutes || 60)) * 100)}%` }}
                  />
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default Subscription;
